import { useGame, useShop } from '@/contexts';
import { useMemo, useState } from 'react';

export interface PlayerProfile {
  name: string;
  username: string;
  createdAt: number;
}

export interface Achievement {
  name: string;
  description: string;
  earned: boolean;
  icon: string;
}

export interface UserProfileStats {
  totalLevelsCompleted: number;
  totalStars: number;
  totalScore: number;
  totalPlayTime: number;
  totalAttempts: number;
  earnedAchievements: number;
  totalAchievements: number;
}

export const useUserProfile = () => {
  const { gameState } = useGame();
  const { shopState, shopManager } = useShop();

  // Profile state
  const [profile, setProfile] = useState<PlayerProfile>(() => {
    const saved = localStorage.getItem('playerProfile');
    if (saved) {
      return JSON.parse(saved);
    }
    return {
      name: 'Player',
      username: `player${Math.floor(Math.random() * 10000)}`,
      createdAt: Date.now(),
    };
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(profile.name);
  const [editUsername, setEditUsername] = useState(profile.username);

  // Calculate aggregated stats
  const stats = useMemo((): UserProfileStats => {
    const totalLevelsCompleted = gameState.completedLevels.length;
    const totalStars = Object.values(gameState.levelStats).reduce(
      (sum, stats) => sum + (stats.stars || 0),
      0
    );
    const totalScore = Object.values(gameState.levelStats).reduce(
      (sum, stats) => sum + (stats.bestScore || 0),
      0
    );
    const totalPlayTime = Object.values(gameState.levelStats).reduce(
      (sum, stats) => sum + (stats.bestTime || 0),
      0
    );
    const totalAttempts = Object.values(gameState.levelStats).reduce(
      (sum, stats) => sum + (stats.totalAttempts || 0),
      0
    );

    return {
      totalLevelsCompleted,
      totalStars,
      totalScore,
      totalPlayTime,
      totalAttempts,
      earnedAchievements: 0, // Will be calculated after achievements
      totalAchievements: 0,
    };
  }, [gameState.completedLevels, gameState.levelStats]);

  // Calculate achievements
  const achievements = useMemo((): Achievement[] => {
    return [
      {
        name: 'First Steps',
        description: 'Complete your first level',
        earned: stats.totalLevelsCompleted > 0,
        icon: 'target',
      },
      {
        name: 'Star Collector',
        description: 'Earn 10 stars',
        earned: stats.totalStars >= 10,
        icon: 'star',
      },
      {
        name: 'Perfectionist',
        description: 'Get 3 stars on any level',
        earned: Object.values(gameState.levelStats).some(levelStats => levelStats.stars === 3),
        icon: 'sparkle',
      },
      {
        name: 'Rich Player',
        description: 'Collect 1000 coins',
        earned: shopState.totalSpent.coins + shopState.coins >= 1000,
        icon: 'coin',
      },
      {
        name: 'Speed Runner',
        description: 'Complete a level in under 30 seconds',
        earned: Object.values(gameState.levelStats).some(levelStats => levelStats.bestTime < 30),
        icon: 'lightning',
      },
      {
        name: 'Persistent',
        description: 'Attempt levels 100 times',
        earned: stats.totalAttempts >= 100,
        icon: 'strength',
      },
    ];
  }, [stats, gameState.levelStats, shopState.totalSpent.coins, shopState.coins]);

  // Update stats with achievement counts
  const finalStats = useMemo((): UserProfileStats => {
    const earnedAchievements = achievements.filter(a => a.earned).length;
    return {
      ...stats,
      earnedAchievements,
      totalAchievements: achievements.length,
    };
  }, [stats, achievements]);

  // Get streak info
  const streakStatus = shopManager?.getDailyRewardStatus() || { streak: 0, canClaim: false };

  // Save profile changes
  const saveProfile = () => {
    const newProfile = {
      ...profile,
      name: editName.trim() || 'Player',
      username: editUsername.trim() || profile.username,
    };
    setProfile(newProfile);
    localStorage.setItem('playerProfile', JSON.stringify(newProfile));
    setIsEditing(false);
  };

  // Cancel editing
  const cancelEdit = () => {
    setIsEditing(false);
    setEditName(profile.name);
    setEditUsername(profile.username);
  };

  // Start editing
  const startEdit = () => {
    setIsEditing(true);
  };

  // Format time display
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  // Format date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString();
  };

  return {
    // Profile data
    profile,
    stats: finalStats,
    achievements,
    streakStatus,

    // Editing state
    isEditing,
    editName,
    editUsername,
    setEditName,
    setEditUsername,

    // Actions
    saveProfile,
    cancelEdit,
    startEdit,

    // Utilities
    formatTime,
    formatDate,
  };
};
